import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DEFAULT_LOGO_URL } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import { hasInvoicePipeline } from '../../constants/productModule';
import { canAccessIntegrations } from '../../utils/userRoles';

// Inline SVG-ikoner så att de alltid syns (ingen extern paketberoende)
const iconSize = 22;
const iconClass = 'block shrink-0';

const IconInvoices = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" x2="8" y1="13" y2="13" />
    <line x1="16" x2="8" y1="17" y2="17" />
    <line x1="10" x2="8" y1="9" y2="9" />
  </svg>
);

const IconLink = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const IconSettings = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconBook = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    <path d="M8 7h6" />
    <path d="M8 11h8" />
  </svg>
);

/** Suppliers – truck / delivery metaphor */
const IconSuppliers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
    <path d="M15 18H9" />
    <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
    <circle cx="17" cy="18" r="2" />
    <circle cx="7" cy="18" r="2" />
  </svg>
);

/** Customers – building / buyer metaphor */
const IconCustomers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4" />
    <path d="M10 10h4" />
    <path d="M10 14h4" />
    <path d="M10 18h4" />
  </svg>
);

/** FX – dollar sign */
const IconFxExposure = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <line x1="12" x2="12" y1="2" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const IconChevron = ({ expanded }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`block shrink-0 transition-transform duration-200 ${expanded ? 'rotate-0' : '-rotate-90'}`}
    aria-hidden
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/** Counterparty / party screening */
const IconCounterpartyScreening = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={iconClass}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const navLinkClass = (isActive) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
    isActive
      ? 'bg-primary-100 text-primary-700 border-l-4 border-primary-500'
      : 'text-gray-700 hover:bg-gray-200'
  }`;

const navChildLinkClass = (isActive) =>
  `block rounded-md py-2 pl-3 pr-3 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-primary-100 text-primary-700 border-l-4 border-primary-500 -ml-px'
      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
  }`;

const navGroupsAll = [
  {
    id: 'invoices',
    label: 'Invoices',
    to: '/dashboard',
    Icon: IconInvoices,
    requiresInvoiceModule: true,
    children: [
      { to: '/invoices', label: 'Scanned Invoices', end: false },
      { to: '/upload', label: 'Upload Invoices', end: true },
    ],
  },
];

const navItemsMain = [
  { to: '/suppliers', label: 'Suppliers', Icon: IconSuppliers },
  { to: '/customers', label: 'Customers', Icon: IconCustomers },
  { to: '/counterparty-screening', label: 'Counterparty Screening', Icon: IconCounterpartyScreening },
  {
    to: '/currency-exposure',
    label: 'FX Exposure',
    Icon: IconFxExposure,
    requiresInvoiceModule: true,
    requiresCurrencyModule: true,
  },
];

const navItemsBottom = [
  { to: '/integrations', label: 'Integrations', Icon: IconLink, requiresIntegrationsAccess: true },
  { to: '/knowledge-hub', label: 'Knowledge Hub', Icon: IconBook },
];

const bottomItem = { to: '/settings', label: 'Settings', Icon: IconSettings };

function isNavChildActive(pathname, child) {
  if (child.end) return pathname === child.to;
  return pathname === child.to || pathname.startsWith(`${child.to}/`);
}

function isInvoicesGroupChildActive(pathname, group) {
  return group.children.some((child) => isNavChildActive(pathname, child));
}

function filterNavItems(items, { profileLoading, productModule, currencyModuleEnabled, role }) {
  return items.filter((item) => {
    if (item.requiresInvoiceModule) {
      if (profileLoading) return false;
      if (!hasInvoicePipeline(productModule)) return false;
    }
    if (item.requiresCurrencyModule) {
      if (profileLoading) return false;
      if (!currencyModuleEnabled) return false;
    }
    if (item.requiresIntegrationsAccess) {
      return !profileLoading && canAccessIntegrations(role);
    }
    return true;
  });
}

function navItemUsesEnd(to) {
  return (
    to === '/currency-exposure' ||
    to === '/suppliers' ||
    to === '/customers' ||
    to === '/counterparty-screening' ||
    to === '/integrations' ||
    to === '/knowledge-hub'
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const { role, loading: profileLoading, productModule, currencyModuleEnabled } = useUserProfile(user);
  const location = useLocation();
  const SettingsIcon = bottomItem.Icon;
  const [invoicesExpanded, setInvoicesExpanded] = useState(true);

  const showInvoiceModule = !profileLoading && hasInvoicePipeline(productModule);

  useEffect(() => {
    const invoicesGroup = navGroupsAll.find((g) => g.id === 'invoices');
    if (invoicesGroup && isInvoicesGroupChildActive(location.pathname, invoicesGroup)) {
      setInvoicesExpanded(true);
    }
  }, [location.pathname]);

  const navGroups = navGroupsAll.filter((group) => {
    if (group.requiresInvoiceModule) return showInvoiceModule;
    return true;
  });

  const filterContext = { profileLoading, productModule, currencyModuleEnabled, role };
  const mainNavItems = filterNavItems(navItemsMain, filterContext);
  const bottomNavItems = filterNavItems(navItemsBottom, filterContext);

  const renderNavItem = (item) => {
    const Icon = item.Icon;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) => navLinkClass(isActive)}
        end={navItemUsesEnd(item.to)}
      >
        {Icon && (
          <span className="flex shrink-0 items-center justify-center w-6 h-6 text-current" aria-hidden>
            <Icon />
          </span>
        )}
        <span className="leading-none">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="w-64 h-screen bg-gray-100 border-r border-gray-200 flex flex-col overflow-hidden">
      <div className="h-14 px-4 border-b border-gray-200 flex items-center gap-3 shrink-0">
        <img
          src={DEFAULT_LOGO_URL}
          alt="Svenry"
          className="h-8 w-auto object-contain"
        />
        <span className="text-xl font-semibold text-gray-800">Svenry</span>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto min-h-0">
        {navGroups.map((group) => {
          const GroupIcon = group.Icon;
          const overviewActive = group.to ? location.pathname === group.to : false;
          const childActive = group.children.some((child) =>
            isNavChildActive(location.pathname, child)
          );
          const groupActive = overviewActive || childActive;
          const expanded = group.id === 'invoices' ? invoicesExpanded : true;

          return (
            <div key={group.id} className="space-y-0.5">
              <div className="flex items-center gap-0.5">
                <NavLink
                  to={group.to}
                  end
                  className={({ isActive }) =>
                    `flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-700 border-l-4 border-primary-500'
                        : groupActive
                          ? 'text-primary-700 hover:bg-gray-200'
                          : 'text-gray-800 hover:bg-gray-200'
                    }`
                  }
                >
                  {GroupIcon && (
                    <span className="flex shrink-0 items-center justify-center w-6 h-6 text-current" aria-hidden>
                      <GroupIcon />
                    </span>
                  )}
                  <span className="leading-none truncate">{group.label}</span>
                </NavLink>
                <button
                  type="button"
                  onClick={() => setInvoicesExpanded((open) => !open)}
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Hide invoice sub-menu' : 'Show invoice sub-menu'}
                  className="shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
                >
                  <IconChevron expanded={expanded} />
                </button>
              </div>
              {expanded && (
                <div className="ml-6 border-l border-gray-300 space-y-0.5 pl-2">
                  {group.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end={child.end}
                      className={({ isActive }) => navChildLinkClass(isActive)}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {mainNavItems.map(renderNavItem)}
      </nav>
      <div className="shrink-0 border-t border-gray-200">
        {bottomNavItems.length > 0 && (
          <div className="px-3 py-2 space-y-0.5">
            {bottomNavItems.map(renderNavItem)}
          </div>
        )}
        <div className="px-3 h-[72px] border-t border-gray-200 flex items-stretch">
        <NavLink
          to={bottomItem.to}
          className={({ isActive }) =>
            `flex flex-1 items-center gap-3 px-3 rounded-md text-sm font-medium transition-colors min-h-0 ${
              isActive
                ? 'bg-primary-100 text-primary-700 border-l-4 border-primary-500'
                : 'text-gray-700 hover:bg-gray-200'
            }`
          }
        >
          {SettingsIcon && (
            <span className="flex shrink-0 items-center justify-center w-6 h-6 text-current" aria-hidden>
              <SettingsIcon />
            </span>
          )}
          <span className="leading-none">{bottomItem.label}</span>
        </NavLink>
        </div>
      </div>
    </aside>
  );
}
